import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Calculator,
  Home,
  Landmark,
  ShieldCheck,
  RefreshCw,
  Wallet,
  ArrowRight,
  Mail,
  X,
  Check,
  ChevronDown,
  Download,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/landing/Footer";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const SITE = "https://lagooncityrealestate.ca";

/* ---------- shared helpers (Canadian mortgage math) ---------- */
const FREQ = { Monthly: 12, "Semi-monthly": 24, "Bi-weekly": 26, Weekly: 52 };

// Canadian mortgages compound semi-annually — convert to the periodic rate.
const periodicRate = (annualPct, perYear) => Math.pow(1 + annualPct / 100 / 2, 2 / perYear) - 1;

const payment = (principal, annualPct, years, perYear) => {
  const n = years * perYear;
  const i = periodicRate(annualPct, perYear);
  if (n <= 0) return 0;
  if (i === 0) return principal / n;
  return (principal * i) / (1 - Math.pow(1 + i, -n));
};

const money = (v) =>
  isFinite(v)
    ? v.toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 })
    : "—";
const money2 = (v) =>
  isFinite(v)
    ? v.toLocaleString("en-CA", { style: "currency", currency: "CAD", minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "—";
const num = (v) => {
  const n = parseFloat(String(v).replace(/[^0-9.]/g, ""));
  return isFinite(n) ? n : 0;
};

/* ---------- small UI primitives ---------- */
const Field = ({ label, prefix, suffix, value, onChange, testid, step, placeholder }) => (
  <label className="block">
    <span className="block text-xs font-mono tracking-[0.15em] uppercase text-[#E890A7]/70 mb-2">{label}</span>
    <div className="flex items-center rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 focus-within:border-[#E890A7]/60 transition-colors">
      {prefix && <span className="pl-4 text-slate-400 select-none">{prefix}</span>}
      <input
        data-testid={testid}
        type="number"
        step={step || "any"}
        inputMode="decimal"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent px-4 py-3 text-slate-100 outline-none placeholder:text-slate-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      {suffix && <span className="pr-4 text-slate-400 select-none">{suffix}</span>}
    </div>
  </label>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="block text-xs font-mono tracking-[0.15em] uppercase text-[#E890A7]/70 mb-2">{label}</span>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60 transition-colors"
    >
      {options.map((o) => (
        <option key={String(o.value ?? o)} value={o.value ?? o} className="bg-[#151218]">
          {o.label ?? o}
        </option>
      ))}
    </select>
  </label>
);

const Result = ({ label, value, big, testid }) => (
  <div className="flex items-baseline justify-between gap-4 py-2 border-b border-[#E890A7]/10 last:border-0">
    <span className={`font-light ${big ? "text-slate-200" : "text-slate-400 text-sm"}`}>{label}</span>
    <span
      data-testid={testid}
      className={`font-display tabular-nums ${big ? "text-2xl sm:text-3xl text-[#F2A7BA]" : "text-lg text-slate-100"}`}
    >
      {value}
    </span>
  </div>
);

const CalcCard = ({ id, icon: Icon, tag, title, desc, children, testid }) => (
  <motion.section
    id={id}
    data-testid={testid}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6 }}
    className="scroll-mt-24 rounded-[2rem] border border-[#E890A7]/15 bg-[#151218]/70 p-7 sm:p-10"
  >
    <div className="flex items-center gap-3 mb-2">
      <span className="grid place-items-center h-11 w-11 rounded-2xl bg-[#E890A7]/12 border border-[#E890A7]/25">
        <Icon size={20} className="text-[#E890A7]" />
      </span>
      <div>
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E890A7]/70">{tag}</p>
        <h2 className="font-display font-light text-2xl sm:text-3xl leading-tight">{title}</h2>
      </div>
    </div>
    <p className="text-slate-400 font-light text-sm mb-7 max-w-2xl">{desc}</p>
    {children}
  </motion.section>
);

/* ---------- Result actions: PDF + Email ---------- */
const downloadPdf = async (calculator, inputs, summary) => {
  const res = await api.post(
    "/calculators/pdf",
    { calculator, inputs, summary },
    { responseType: "blob" }
  );
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${calculator.replace(/[^a-z0-9]+/gi, "-")}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

function ResultActions({ calculator, inputs, summary }) {
  const [busy, setBusy] = useState(false);
  const slug = calculator.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const onPdf = async () => {
    setBusy(true);
    try {
      await downloadPdf(calculator, inputs, summary);
      trackEvent("calculator_pdf", { calculator });
    } catch (e) {
      /* ignore */
    }
    setBusy(false);
  };
  return (
    <div className="mt-5 grid grid-cols-2 gap-3">
      <button
        data-testid={`download-pdf-btn-${slug}`}
        onClick={onPdf}
        disabled={busy}
        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#E890A7] text-[#0D0B0E] px-4 py-2.5 text-sm font-semibold hover:bg-[#F2A7BA] transition-colors disabled:opacity-60"
      >
        <Download size={15} /> {busy ? "Preparing…" : "Save PDF"}
      </button>
      <EmailResults calculator={calculator} inputs={inputs} summary={summary} />
    </div>
  );
}

/* ---------- Email results modal ---------- */
function EmailResults({ calculator, inputs, summary }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setStatus("sending");
    try {
      await api.post("/calculators/email", { name, email, calculator, inputs, summary });
      trackEvent("generate_lead", { method: "calculator", calculator });
      setStatus("done");
    } catch (err) {
      setStatus("error");
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => {
      setStatus("idle");
      setName("");
      setEmail("");
    }, 200);
  };

  return (
    <>
      <button
        data-testid={`email-results-btn-${calculator.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center gap-2 rounded-full border border-[#E890A7]/40 text-[#F2A7BA] px-4 py-2.5 text-sm hover:bg-[#E890A7]/10 transition-colors"
      >
        <Mail size={15} /> Email results
      </button>

      {open && (
        <div
          data-testid="email-results-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={close}
        >
          <div
            className="w-full max-w-md rounded-[1.75rem] border border-[#E890A7]/25 bg-[#151218] p-7 sm:p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="email-results-close"
              onClick={close}
              aria-label="Close"
              className="absolute right-5 top-5 text-slate-400 hover:text-[#F2A7BA] transition-colors"
            >
              <X size={20} />
            </button>

            {status === "done" ? (
              <div className="text-center py-6" data-testid="email-results-success">
                <span className="grid place-items-center h-14 w-14 rounded-full bg-[#E890A7]/15 border border-[#E890A7]/30 mx-auto mb-5">
                  <Check size={26} className="text-[#E890A7]" />
                </span>
                <h3 className="font-display text-2xl">Check your inbox</h3>
                <p className="mt-3 text-slate-400 font-light text-sm">
                  Your {calculator} results are on their way to {email}. Joselle will follow up if you'd like a hand.
                </p>
                <button
                  onClick={close}
                  className="mt-6 rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-6 py-2.5 hover:bg-[#F2A7BA] transition-colors"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={submit}>
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E890A7]/70 mb-2">
                  Email my results
                </p>
                <h3 className="font-display font-light text-2xl leading-tight mb-1">{calculator}</h3>
                <p className="text-slate-400 font-light text-sm mb-6">
                  We'll send a copy to you and let Joselle know you're crunching numbers.
                </p>
                <div className="space-y-4">
                  <label className="block">
                    <span className="block text-xs font-mono tracking-[0.15em] uppercase text-[#E890A7]/70 mb-2">Your name</span>
                    <input
                      data-testid="email-results-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="w-full rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60 transition-colors"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-mono tracking-[0.15em] uppercase text-[#E890A7]/70 mb-2">Your email</span>
                    <input
                      data-testid="email-results-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60 transition-colors"
                    />
                  </label>
                </div>
                {status === "error" && (
                  <p data-testid="email-results-error" className="mt-4 text-sm text-red-400">
                    Something went wrong sending your results. Please try again.
                  </p>
                )}
                <button
                  data-testid="email-results-submit"
                  type="submit"
                  disabled={status === "sending"}
                  className="mt-6 w-full rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-6 py-3 hover:bg-[#F2A7BA] transition-colors disabled:opacity-60"
                >
                  {status === "sending" ? "Sending…" : "Send my results"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const amortOptions = [5, 10, 15, 20, 25, 30].map((y) => ({ value: y, label: `${y} years` }));

/* ---------- 1. Mortgage payment + amortization schedule ---------- */
function MortgagePaymentCalc({ rate, setRate }) {
  const [price, setPrice] = useState("650000");
  const [down, setDown] = useState("130000");
  const [years, setYears] = useState(25);
  const [freq, setFreq] = useState("Monthly");
  const [showSchedule, setShowSchedule] = useState(false);

  const principal = Math.max(num(price) - num(down), 0);
  const perYear = FREQ[freq];
  const pmt = payment(principal, num(rate), Number(years), perYear);
  const totalPaid = pmt * Number(years) * perYear;
  const totalInterest = totalPaid - principal;
  const monthly = payment(principal, num(rate), Number(years), 12);

  const schedule = useMemo(() => {
    const i = periodicRate(num(rate), perYear);
    const n = Number(years) * perYear;
    let bal = principal;
    const rows = [];
    let yInt = 0;
    let yPrin = 0;
    let yPaid = 0;
    for (let k = 1; k <= n; k++) {
      const interest = bal * i;
      let prin = pmt - interest;
      if (prin > bal) prin = bal;
      bal = Math.max(bal - prin, 0);
      yInt += interest;
      yPrin += prin;
      yPaid += interest + prin;
      if (k % perYear === 0 || k === n) {
        rows.push({ year: Math.ceil(k / perYear), interest: yInt, principal: yPrin, paid: yPaid, balance: bal });
        yInt = 0;
        yPrin = 0;
        yPaid = 0;
      }
    }
    return rows;
  }, [principal, rate, years, perYear, pmt]);

  const emailInputs = [
    { label: "Home price", value: money(num(price)) },
    { label: "Down payment", value: money(num(down)) },
    { label: "Interest rate", value: `${num(rate)}%` },
    { label: "Amortization", value: `${years} years` },
    { label: "Payment frequency", value: freq },
  ];
  const emailSummary = [
    { label: `${freq} payment`, value: money2(pmt) },
    { label: "Mortgage amount", value: money(principal) },
    { label: "Total interest paid", value: money(totalInterest) },
    { label: "Total of payments", value: money(totalPaid) },
  ];

  return (
    <CalcCard
      id="mortgage-payment"
      testid="calc-mortgage-payment"
      icon={Home}
      tag="Mortgage Loan"
      title="Mortgage Payment Calculator"
      desc="Estimate your regular payment and see how much interest you'll pay over the life of the loan. Uses Canadian semi-annual compounding."
    >
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <Field label="Home price" prefix="$" value={price} onChange={setPrice} testid="mp-price" />
          <Field label="Down payment" prefix="$" value={down} onChange={setDown} testid="mp-down" />
          <Field label="Interest rate (today's rate)" suffix="%" value={rate} onChange={setRate} testid="mp-rate" />
          <div className="grid grid-cols-2 gap-4">
            <Select label="Amortization" value={years} onChange={setYears} options={amortOptions} testid="mp-amort" />
            <Select label="Frequency" value={freq} onChange={setFreq} options={Object.keys(FREQ)} testid="mp-freq" />
          </div>
        </div>
        <div className="rounded-2xl bg-[#0D0B0E]/60 border border-[#E890A7]/15 p-6 self-start">
          <Result label={`${freq} payment`} value={money2(pmt)} big testid="mp-payment" />
          {freq !== "Monthly" && <Result label="Equivalent monthly" value={money2(monthly)} testid="mp-monthly" />}
          <Result label="Mortgage amount" value={money(principal)} testid="mp-principal" />
          <Result label="Total interest paid" value={money(totalInterest)} testid="mp-interest" />
          <Result label="Total of payments" value={money(totalPaid)} testid="mp-total" />
          <ResultActions calculator="Mortgage Payment Calculator" inputs={emailInputs} summary={emailSummary} />
        </div>
      </div>

      {/* Amortization schedule */}
      <div className="mt-6">
        <button
          data-testid="mp-schedule-toggle"
          onClick={() => setShowSchedule((v) => !v)}
          className="inline-flex items-center gap-2 text-sm text-[#F2A7BA] hover:text-[#E890A7] transition-colors"
        >
          <ChevronDown size={16} className={`transition-transform duration-300 ${showSchedule ? "rotate-180" : ""}`} />
          {showSchedule ? "Hide" : "Show"} year-by-year amortization schedule
        </button>
        {showSchedule && (
          <div data-testid="mp-schedule" className="mt-4 rounded-2xl border border-[#E890A7]/15 overflow-hidden">
            <div className="max-h-80 overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#1E1A23] text-[#E890A7]/80">
                  <tr>
                    <th className="text-left font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-3">Year</th>
                    <th className="text-right font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-3">Principal</th>
                    <th className="text-right font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-3">Interest</th>
                    <th className="text-right font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-3">Balance</th>
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  {schedule.map((r) => (
                    <tr key={r.year} className="border-t border-[#E890A7]/8">
                      <td className="px-4 py-2.5 tabular-nums">{r.year}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{money(r.principal)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-400">{money(r.interest)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-[#F2A7BA]">{money(r.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </CalcCard>
  );
}

/* ---------- 2. Affordability ---------- */
function AffordabilityCalc({ rate, setRate }) {
  const [income, setIncome] = useState("120000");
  const [debts, setDebts] = useState("500");
  const [down, setDown] = useState("100000");
  const [years, setYears] = useState(25);
  const [tax, setTax] = useState("4200");
  const [heat, setHeat] = useState("150");
  const [stress, setStress] = useState("yes");

  const gmi = num(income) / 12;
  const monthlyTax = num(tax) / 12;
  const qualRate = stress === "yes" ? Math.max(num(rate) + 2, 5.25) : num(rate);

  const gdsCap = gmi * 0.32;
  const tdsCap = gmi * 0.44 - num(debts);
  const pithCap = Math.max(Math.min(gdsCap, tdsCap), 0);
  const piBudget = Math.max(pithCap - monthlyTax - num(heat), 0);

  const i = periodicRate(qualRate, 12);
  const n = Number(years) * 12;
  const maxMortgage = i === 0 ? piBudget * n : (piBudget * (1 - Math.pow(1 + i, -n))) / i;
  const maxPrice = maxMortgage + num(down);
  const actualPmt = payment(maxMortgage, num(rate), Number(years), 12);

  const emailInputs = [
    { label: "Gross annual income", value: money(num(income)) },
    { label: "Monthly debts", value: money(num(debts)) },
    { label: "Down payment", value: money(num(down)) },
    { label: "Property tax / yr", value: money(num(tax)) },
    { label: "Heating / mo", value: money(num(heat)) },
    { label: "Interest rate", value: `${num(rate)}%` },
    { label: "Amortization", value: `${years} years` },
    { label: "Stress test", value: stress === "yes" ? "Applied" : "Not applied" },
  ];
  const emailSummary = [
    { label: "Maximum home price", value: money(maxPrice) },
    { label: "Maximum mortgage", value: money(maxMortgage) },
    { label: "Est. monthly payment", value: money2(actualPmt) },
    { label: "Qualifying rate used", value: `${qualRate.toFixed(2)}%` },
  ];

  return (
    <CalcCard
      id="affordability"
      testid="calc-affordability"
      icon={Wallet}
      tag="Affordability"
      title="Mortgage Affordability Calculator"
      desc="Find out roughly how much home you can afford, based on your income and the lender GDS/TDS ratios (32% / 44%). Includes the federal stress test."
    >
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <Field label="Gross annual household income" prefix="$" value={income} onChange={setIncome} testid="af-income" />
          <Field label="Monthly debt payments (loans, cards)" prefix="$" value={debts} onChange={setDebts} testid="af-debts" />
          <Field label="Down payment" prefix="$" value={down} onChange={setDown} testid="af-down" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Property tax / yr" prefix="$" value={tax} onChange={setTax} testid="af-tax" />
            <Field label="Heating / mo" prefix="$" value={heat} onChange={setHeat} testid="af-heat" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Interest rate (today's rate)" suffix="%" value={rate} onChange={setRate} testid="af-rate" />
            <Select label="Amortization" value={years} onChange={setYears} options={amortOptions} testid="af-amort" />
          </div>
          <Select
            label="Apply federal stress test"
            value={stress}
            onChange={setStress}
            options={[{ value: "yes", label: "Yes — recommended" }, { value: "no", label: "No" }]}
            testid="af-stress"
          />
        </div>
        <div className="rounded-2xl bg-[#0D0B0E]/60 border border-[#E890A7]/15 p-6 self-start">
          <Result label="Maximum home price" value={money(maxPrice)} big testid="af-max-price" />
          <Result label="Maximum mortgage" value={money(maxMortgage)} testid="af-max-mortgage" />
          <Result label="Est. monthly payment" value={money2(actualPmt)} testid="af-payment" />
          <Result label="Qualifying rate used" value={`${qualRate.toFixed(2)}%`} testid="af-qual-rate" />
          <p className="mt-4 text-xs text-slate-500 font-light">
            Lenders qualify you at the greater of your rate + 2% or 5.25%. This is a guide only — speak with a
            mortgage professional for a firm pre-approval.
          </p>
          <ResultActions calculator="Affordability Calculator" inputs={emailInputs} summary={emailSummary} />
        </div>
      </div>
    </CalcCard>
  );
}

/* ---------- 3. Ontario Land Transfer Tax ---------- */
const ontarioLTT = (price) => {
  const brackets = [
    [55000, 0.005],
    [250000, 0.01],
    [400000, 0.015],
    [2000000, 0.02],
    [Infinity, 0.025],
  ];
  let tax = 0;
  let prev = 0;
  for (const [cap, r] of brackets) {
    if (price <= prev) break;
    tax += (Math.min(price, cap) - prev) * r;
    prev = cap;
  }
  return tax;
};

function LandTransferCalc() {
  const [price, setPrice] = useState("650000");
  const [firstTime, setFirstTime] = useState("no");

  const tax = ontarioLTT(num(price));
  const rebate = firstTime === "yes" ? Math.min(tax, 4000) : 0;
  const net = tax - rebate;

  const emailInputs = [
    { label: "Purchase price", value: money(num(price)) },
    { label: "First-time buyer", value: firstTime === "yes" ? "Yes" : "No" },
  ];
  const emailSummary = [
    { label: "Land transfer tax due", value: money(net) },
    { label: "Provincial tax", value: money(tax) },
    ...(rebate > 0 ? [{ label: "First-time buyer rebate", value: `- ${money(rebate)}` }] : []),
  ];

  return (
    <CalcCard
      id="land-transfer-tax"
      testid="calc-land-transfer"
      icon={Landmark}
      tag="Closing Costs"
      title="Ontario Land Transfer Tax Calculator"
      desc="Estimate the provincial land transfer tax due on closing. Lagoon City is in the Township of Ramara, so no additional municipal (Toronto) tax applies. First-time buyers may qualify for a rebate up to $4,000."
    >
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <Field label="Purchase price" prefix="$" value={price} onChange={setPrice} testid="ltt-price" />
          <Select
            label="First-time home buyer?"
            value={firstTime}
            onChange={setFirstTime}
            options={[{ value: "no", label: "No" }, { value: "yes", label: "Yes" }]}
            testid="ltt-firsttime"
          />
        </div>
        <div className="rounded-2xl bg-[#0D0B0E]/60 border border-[#E890A7]/15 p-6 self-start">
          <Result label="Land transfer tax due" value={money(net)} big testid="ltt-net" />
          <Result label="Provincial tax" value={money(tax)} testid="ltt-tax" />
          {rebate > 0 && <Result label="First-time buyer rebate" value={`- ${money(rebate)}`} testid="ltt-rebate" />}
          <ResultActions calculator="Land Transfer Tax Calculator" inputs={emailInputs} summary={emailSummary} />
        </div>
      </div>
    </CalcCard>
  );
}

/* ---------- 4. CMHC premium ---------- */
function CMHCCalc() {
  const [price, setPrice] = useState("650000");
  const [down, setDown] = useState("40000");
  const [years, setYears] = useState(25);

  const result = useMemo(() => {
    const p = num(price);
    const d = num(down);
    const mortgage = Math.max(p - d, 0);
    const dpPct = p > 0 ? d / p : 0;
    const ltv = p > 0 ? mortgage / p : 0;
    if (dpPct >= 0.2) return { mortgage, premium: 0, rate: 0, insured: false, total: mortgage };
    let rate = 0.028;
    if (ltv > 0.9) rate = 0.04;
    else if (ltv > 0.85) rate = 0.031;
    let premium = mortgage * rate;
    if (Number(years) > 25) premium += mortgage * 0.002;
    return { mortgage, premium, rate, insured: true, total: mortgage + premium };
  }, [price, down, years]);

  const emailInputs = [
    { label: "Purchase price", value: money(num(price)) },
    { label: "Down payment", value: money(num(down)) },
    { label: "Amortization", value: `${years} years` },
  ];
  const emailSummary = result.insured
    ? [
        { label: "CMHC premium", value: money(result.premium) },
        { label: "Premium rate", value: `${(result.rate * 100).toFixed(2)}%` },
        { label: "Base mortgage", value: money(result.mortgage) },
        { label: "Total mortgage incl. premium", value: money(result.total) },
      ]
    : [{ label: "CMHC premium", value: money(0) }, { label: "Note", value: "20%+ down — no insurance required" }];

  return (
    <CalcCard
      id="cmhc-premium"
      testid="calc-cmhc"
      icon={ShieldCheck}
      tag="Mortgage Insurance"
      title="CMHC Premium Calculator"
      desc="If your down payment is under 20%, mortgage default insurance is required. Estimate the premium, which is added to your mortgage. A surcharge applies to amortizations over 25 years."
    >
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <Field label="Purchase price" prefix="$" value={price} onChange={setPrice} testid="cmhc-price" />
          <Field label="Down payment" prefix="$" value={down} onChange={setDown} testid="cmhc-down" />
          <Select label="Amortization" value={years} onChange={setYears} options={amortOptions} testid="cmhc-amort" />
        </div>
        <div className="rounded-2xl bg-[#0D0B0E]/60 border border-[#E890A7]/15 p-6 self-start">
          {result.insured ? (
            <>
              <Result label="CMHC premium" value={money(result.premium)} big testid="cmhc-premium" />
              <Result label="Premium rate" value={`${(result.rate * 100).toFixed(2)}%`} testid="cmhc-rate" />
              <Result label="Base mortgage" value={money(result.mortgage)} testid="cmhc-mortgage" />
              <Result label="Total mortgage incl. premium" value={money(result.total)} testid="cmhc-total" />
            </>
          ) : (
            <div data-testid="cmhc-none">
              <Result label="CMHC premium" value={money(0)} big />
              <p className="mt-4 text-sm text-slate-400 font-light">
                Your down payment is 20% or more, so no mortgage default insurance is required.
              </p>
            </div>
          )}
          <ResultActions calculator="CMHC Premium Calculator" inputs={emailInputs} summary={emailSummary} />
        </div>
      </div>
    </CalcCard>
  );
}

/* ---------- 5. Renewal ---------- */
function RenewalCalc({ rate, setRate }) {
  const [balance, setBalance] = useState("420000");
  const [remaining, setRemaining] = useState(20);
  const [oldRate, setOldRate] = useState("2.49");
  const [freq, setFreq] = useState("Monthly");

  const perYear = FREQ[freq];
  const oldPmt = payment(num(balance), num(oldRate), Number(remaining), perYear);
  const newPmt = payment(num(balance), num(rate), Number(remaining), perYear);
  const diff = newPmt - oldPmt;

  const emailInputs = [
    { label: "Balance at renewal", value: money(num(balance)) },
    { label: "Remaining amortization", value: `${remaining} years` },
    { label: "Current rate", value: `${num(oldRate)}%` },
    { label: "New rate", value: `${num(rate)}%` },
    { label: "Payment frequency", value: freq },
  ];
  const emailSummary = [
    { label: "New payment", value: money2(newPmt) },
    { label: "Current payment", value: money2(oldPmt) },
    { label: diff >= 0 ? "Increase per payment" : "Decrease per payment", value: `${diff >= 0 ? "+" : "-"} ${money2(Math.abs(diff))}` },
    { label: "Change per year", value: `${diff >= 0 ? "+" : "-"} ${money(Math.abs(diff) * perYear)}` },
  ];

  return (
    <CalcCard
      id="renewal"
      testid="calc-renewal"
      icon={RefreshCw}
      tag="Renewal"
      title="Mortgage Renewal Calculator"
      desc="Coming up for renewal? See how your payment changes at today's rates compared with your current rate, over your remaining amortization."
    >
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <Field label="Balance at renewal" prefix="$" value={balance} onChange={setBalance} testid="rn-balance" />
          <Select
            label="Remaining amortization"
            value={remaining}
            onChange={setRemaining}
            options={amortOptions}
            testid="rn-amort"
          />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Current rate" suffix="%" value={oldRate} onChange={setOldRate} testid="rn-old-rate" />
            <Field label="New rate (today's rate)" suffix="%" value={rate} onChange={setRate} testid="rn-new-rate" />
          </div>
          <Select label="Frequency" value={freq} onChange={setFreq} options={Object.keys(FREQ)} testid="rn-freq" />
        </div>
        <div className="rounded-2xl bg-[#0D0B0E]/60 border border-[#E890A7]/15 p-6 self-start">
          <Result label="New payment" value={money2(newPmt)} big testid="rn-new-pmt" />
          <Result label="Current payment" value={money2(oldPmt)} testid="rn-old-pmt" />
          <Result
            label={diff >= 0 ? "Increase per payment" : "Decrease per payment"}
            value={`${diff >= 0 ? "+" : "-"} ${money2(Math.abs(diff))}`}
            testid="rn-diff"
          />
          <Result label="Change per year" value={`${diff >= 0 ? "+" : "-"} ${money(Math.abs(diff) * perYear)}`} testid="rn-diff-year" />
          <ResultActions calculator="Mortgage Renewal Calculator" inputs={emailInputs} summary={emailSummary} />
        </div>
      </div>
    </CalcCard>
  );
}

const TOC = [
  { id: "mortgage-payment", label: "Mortgage Payment", icon: Home },
  { id: "affordability", label: "Affordability", icon: Wallet },
  { id: "land-transfer-tax", label: "Land Transfer Tax", icon: Landmark },
  { id: "cmhc-premium", label: "CMHC Premium", icon: ShieldCheck },
  { id: "renewal", label: "Renewal", icon: RefreshCw },
];

export default function Calculators() {
  const [rate, setRate] = useState("4.6");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const canonical = `${SITE}/calculators`;
  const title = "Mortgage & Land Transfer Calculators — Lagoon City Real Estate | Joselle Alves";
  const desc =
    "Free Ontario mortgage calculators from Lagoon City Realtor® Joselle Alves — estimate your mortgage payment, affordability, Ontario land transfer tax, CMHC insurance premium and renewal payments.";

  return (
    <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <meta
          name="keywords"
          content="Ontario mortgage calculator, mortgage payment calculator, affordability calculator, Ontario land transfer tax calculator, CMHC premium calculator, mortgage renewal calculator, Lagoon City real estate"
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <PageHeader />

      {/* Hero */}
      <section className="relative border-b border-[#E890A7]/10">
        <div className="max-w-5xl mx-auto px-6 pt-20 pb-14 sm:pt-28 sm:pb-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#F2A7BA] mb-4 flex items-center gap-2">
              <Calculator size={14} /> Calculators
            </p>
            <h1
              data-testid="calculators-title"
              className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl"
            >
              Crunch the numbers on your <em className="italic text-[#E890A7]">Lagoon City</em> move
            </h1>
            <p className="mt-6 max-w-2xl text-slate-300 font-light text-lg">
              Free, easy-to-use calculators for buyers and owners in Ontario — from your monthly mortgage payment
              to land transfer tax and CMHC insurance. Estimates only; let's talk for exact numbers.
            </p>

            {/* Global today's rate */}
            <div
              data-testid="global-rate-bar"
              className="mt-8 inline-flex flex-wrap items-center gap-4 rounded-2xl border border-[#E890A7]/25 bg-[#151218]/80 px-5 py-4"
            >
              <span className="text-sm text-slate-300 font-light">
                <span className="text-[#F2A7BA] font-medium">Today's rate</span> — pre-fills every calculator
              </span>
              <div className="flex items-center rounded-full border border-[#E890A7]/30 bg-[#0D0B0E]/60">
                <input
                  data-testid="global-rate-input"
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  className="w-24 bg-transparent px-4 py-2 text-slate-100 text-lg font-display outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="pr-4 text-slate-400">%</span>
              </div>
              <span data-testid="rate-source" className="text-xs text-slate-500 font-light w-full sm:w-auto">
                Typical discounted rate — adjust to match your own mortgage quote
              </span>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              {TOC.map((t) => {
                const Icon = t.icon;
                return (
                  <a
                    key={t.id}
                    href={`#${t.id}`}
                    data-testid={`calc-toc-${t.id}`}
                    className="inline-flex items-center gap-2 rounded-full border border-[#E890A7]/25 px-4 py-2 text-sm text-slate-300 hover:text-[#F2A7BA] hover:border-[#E890A7]/50 transition-colors"
                  >
                    <Icon size={14} className="text-[#E890A7]" /> {t.label}
                  </a>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Calculators */}
      <div className="max-w-5xl mx-auto px-6 py-16 sm:py-20 space-y-10">
        <MortgagePaymentCalc rate={rate} setRate={setRate} />
        <AffordabilityCalc rate={rate} setRate={setRate} />
        <LandTransferCalc />
        <CMHCCalc />
        <RenewalCalc rate={rate} setRate={setRate} />

        <p className="text-xs text-slate-500 font-light text-center max-w-2xl mx-auto pt-4">
          These calculators are provided for general information and estimation only, and do not constitute
          financial, mortgage or legal advice. Rates, tax brackets and insurance premiums change — confirm all
          figures with your mortgage professional, lawyer and lender.
        </p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="rounded-[2rem] border border-[#E890A7]/25 bg-gradient-to-br from-[#1E1A23] to-[#151218] p-8 sm:p-10 text-center"
        >
          <h2 className="font-display font-light text-2xl sm:text-3xl">Ready to run the real numbers?</h2>
          <p className="mt-3 text-slate-400 font-light max-w-xl mx-auto">
            I'll connect you with a trusted local mortgage professional and help you find the right Lagoon City home.
          </p>
          <Link
            data-testid="calculators-cta"
            to="/#contact"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 hover:bg-[#F2A7BA] hover:-translate-y-0.5 transition-all duration-300"
          >
            Talk to Joselle <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
}
